import { useInfiniteQuery, useQueries, useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api/client";

/**
 * Header monitoring reads (task 192): a site's reported policies and a
 * monitored page's scheduled scans. All readable on every plan, so a tenant
 * that downgraded keeps its history.
 */

/** Scans fetched per page of history. */
export const SCAN_PAGE_SIZE = 30;

export function useCspPolicies(siteId: string | undefined, pageId?: string) {
  return useQuery({
    queryKey: ["sites", siteId ?? "", "csp-policies", pageId ?? null],
    queryFn: () =>
      api.pages.cspPolicies(siteId!, pageId ? { pageId } : undefined),
    enabled: !!siteId,
    // Built from reports over the tenant's retention; a minute is fresh.
    staleTime: 60_000,
  });
}

/**
 * Every site's policies at once, for `Policy > Changes` under "all sites"
 * (ADR 013). One query per site, sharing the keys `useCspPolicies` uses, so
 * scoping to a site reads what this already fetched.
 */
export function useCspPoliciesForSites(siteIds: readonly string[]) {
  return useQueries({
    queries: siteIds.map((siteId) => ({
      queryKey: ["sites", siteId, "csp-policies", null],
      queryFn: () => api.pages.cspPolicies(siteId),
      staleTime: 60_000,
    })),
  });
}

/** A page's scans, newest first, a page of history at a time by `nextBefore`. */
export function usePageScans(
  siteId: string | undefined,
  pageId: string | undefined,
) {
  return useInfiniteQuery({
    queryKey: ["sites", siteId ?? "", "pages", pageId ?? "", "scans"],
    queryFn: ({ pageParam }) =>
      api.pages.scans(siteId!, pageId!, {
        limit: SCAN_PAGE_SIZE,
        ...(pageParam ? { before: pageParam } : {}),
      }),
    initialPageParam: null as string | null,
    getNextPageParam: (last) => last.nextBefore,
    enabled: !!siteId && !!pageId,
    // Scans run daily; nothing changes under a minute.
    staleTime: 60_000,
  });
}

export function usePageScan(
  siteId: string | undefined,
  pageId: string | undefined,
  scanId: string | null,
) {
  return useQuery({
    queryKey: ["sites", siteId ?? "", "pages", pageId ?? "", "scans", scanId],
    queryFn: () => api.pages.scan(siteId!, pageId!, scanId!),
    enabled: !!siteId && !!pageId && !!scanId,
    // A stored scan never changes.
    staleTime: Infinity,
  });
}

/** Changes fetched per page of `Headers › Changes` (task 374). */
export const CHANGE_PAGE_SIZE = 50;

/** The window the tab row's badge counts over. */
export const CHANGE_BADGE_DAYS = 7;

/** One site's scans that recorded a change, newest first, by `nextCursor`. */
export function usePageChanges(siteId: string | undefined) {
  return useInfiniteQuery({
    queryKey: ["sites", siteId ?? "", "page-changes"],
    queryFn: ({ pageParam }) =>
      api.pages.changes(siteId!, {
        limit: CHANGE_PAGE_SIZE,
        ...(pageParam ? { cursor: pageParam } : {}),
      }),
    initialPageParam: null as string | null,
    getNextPageParam: (last) => last.nextCursor,
    enabled: !!siteId,
    // Scans run daily; nothing changes under a minute.
    staleTime: 60_000,
  });
}

/**
 * The first page of every site's changes at once, for `Headers › Changes`
 * under "all sites" (ADR 013): one query per site, merged by the page. A
 * site with more than a page says so there rather than paging a merge.
 */
export function usePageChangesForSites(siteIds: readonly string[]) {
  return useQueries({
    queries: siteIds.map((siteId) => ({
      queryKey: ["sites", siteId, "page-changes", "first"],
      queryFn: () => api.pages.changes(siteId, { limit: CHANGE_PAGE_SIZE }),
      staleTime: 60_000,
    })),
  });
}

/**
 * How many scans recorded a change in the last `CHANGE_BADGE_DAYS` days,
 * summed over `siteIds`, for the Changes tab's badge: the list's own query
 * with `limit=0`. Undefined until every site has answered, so the badge
 * never shows a partial sum.
 */
export function useWeeklyChangeCount(
  siteIds: readonly string[],
): number | undefined {
  const results = useQueries({
    queries: siteIds.map((siteId) => ({
      queryKey: ["sites", siteId, "page-changes", "weekly-count"],
      queryFn: () =>
        api.pages.changes(siteId, {
          limit: 0,
          startTime: new Date(
            Date.now() - CHANGE_BADGE_DAYS * 24 * 3600 * 1000,
          ).toISOString(),
        }),
      staleTime: 60_000,
    })),
  });
  if (siteIds.length === 0 || results.some((result) => !result.data)) {
    return undefined;
  }
  return results.reduce((sum, result) => sum + (result.data?.count ?? 0), 0);
}
